// @givmo/app-charity — the ORG SWITCHER (SaaS layer), and the account menu's second door onto it.
//
// The active-org affordance at the top of the shell sidebar. Reads the active org from ActiveOrgContext:
//   - HAS AN ORG: an interactive Menu — the active org on the trigger; the popup lists every org the
//     caller administers (a checked row for the active one, picking one re-scopes the WHOLE app via
//     `setActiveCharityId`) and carries the "Claim another organization" action.
//   - NO ORG (unclaimed) / still-loading / errored: renders nothing (the pages own those states).
//
// ★ WHY EVEN A SINGLE-ORG ADMIN GETS A MENU (CPA-WEB10 / D-CP-36). Removing the Claim nav group made
// this menu the ONLY home for "Claim another organization" — and a single-org admin is precisely who
// needs that action, since nobody reaches two orgs without claiming a second from one.
//
// ★ THE ROWS LIVE IN ONE PLACE (CPA-WEB40, the OM-13 fold): `OrgSwitcherMenuSection` renders the
// organization rows for BOTH doors — this chip's own menu, and the top-right avatar's account menu
// (in both layouts, per the 2026-08-31 ruling: a module page has no sidebar to hold this chip, and
// one consequential control must not have two homes across two chromes of one app). The account-menu
// door renders only ABOVE one organization: at one org the menu's identity block already names it,
// and a "Switch organization" group leading to a list of itself is the decorative control the shell
// contract bans. The chip keeps its menu at one org for the reason above. Same rows, same testids,
// same unsaved-changes guard; both menus are portalled and mounted only while open, and two menus
// cannot be open at once, so the option ids stay unique in the DOM.
//
// ★ THE ROWS ARE `menuitemradio`. The active organization used to be marked only by a check glyph
// carrying `aria-hidden`, so in the accessibility tree the rows read identically — nothing inside the
// menu said which organization you were acting for. `Menu.RadioGroup` + `Menu.RadioItem` expose it
// as `aria-checked` and give set position; the glyph stays and the row is pixel-identical.
//
// ★ AND IT SAYS WHEN AN ORGANIZATION ARRIVES (CPA-WEB46, Jonathan 2026-09-07). A claim is approved by
// someone else, elsewhere, minutes after it is submitted — so the switcher's list changing under a live
// console is the ONE thing here that happens without the person doing it. `NewOrgAnnouncement` names
// the new organization once, on the device that had not seen it, and offers to switch to it.
//
// DATA CONTRACT (Playwright / RTL): `org-switcher` (root), `org-switcher-trigger` (the button),
// `org-switcher-menu` (the popup), `org-switcher-option-<charityId>` (each option),
// `org-switcher-claim-another` (the second-claim action), `org-switcher-active-name` (the active org
// name, on the trigger), `org-switcher-new-org` (the arrival moment) + `-switch` / `-dismiss` (its two
// controls). ⚠ `org-switcher-static` is GONE — there is no longer a non-interactive mode.

import { Avatar, cn, FOCUS_RING, Icon, IconButton, Menu, testIdProp } from '@givmo/design-system';
import { orgLevelWord } from '@givmo/org-console';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Check, CheckCircle2, ChevronsUpDown, Plus, X } from 'lucide-react';
import { useActiveOrg } from './active-org-context';
import { CLAIM_ROUTES } from '../features/claim/claim-routes';
import { useUnsavedChangesGuard } from '../lib/unsaved-changes';

/**
 * The org-level word under an organization's name, shared by the org menu and account identity —
 * "Global Admin" for a Global Admin, and NOTHING for anyone else (`null`).
 *
 * ⚠ THE PACKAGE'S ONE LABELLER (shell contract v3, R6 / T7.3): the org-level role reads "Global
 * Admin" everywhere; "Owner" names a per-module tier only. The wire's `Owner` is unchanged. And since
 * OM-96 (D-CPO-77 item 7) the org-level "Member" is said nowhere: beside an app's own Member or Owner
 * it read as a contradiction, so a regular admin's corner shows their name and the organization's
 * alone.
 */
export function roleLabel(role: string): string | null {
  return orgLevelWord(role);
}

export interface OrgSwitcherMenuSectionProps {
  /** The group's caption. */
  label: string;
  /**
   * Render only above ONE organization — the account menu's door. The chip's own menu passes
   * `false`: it renders at one org because it is the only home for "Claim another organization".
   */
  aboveOneOnly?: boolean;
  /** Close the section with a separator — for a menu that carries OTHER rows after it. */
  trailingSeparator?: boolean;
}

/**
 * The organization rows + the claim-another action, from ONE implementation (see the header).
 *
 * Renders nothing until an organization is positively resolved, and — for the account-menu door —
 * nothing below two organizations.
 */
export function OrgSwitcherMenuSection({
  label,
  aboveOneOnly = false,
  trailingSeparator = false,
}: OrgSwitcherMenuSectionProps) {
  const navigate = useNavigate();
  const { hasUnsavedChanges, confirmDiscard } = useUnsavedChangesGuard();
  const { memberships, activeMembership, activeCharityId, setActiveCharityId, isReady } =
    useActiveOrg();

  if (!isReady || activeMembership === null) return null;
  if (aboveOneOnly && memberships.length < 2) return null;

  /**
   * Both menu actions leave the current screen — one re-scopes every page, the other navigates away —
   * so both go through the same unsaved-changes confirm. Starting a second claim from a half-edited
   * profile loses exactly as much work as switching org does.
   */
  function whenDiscardable(act: () => void) {
    if (!hasUnsavedChanges) {
      act();
      return;
    }
    void confirmDiscard().then((confirmed) => {
      if (confirmed) act();
    });
  }

  return (
    <>
      {/* Base UI requires <Menu.GroupLabel> to live inside a <Menu.Group>. */}
      <Menu.Group>
        <Menu.GroupLabel saas>{label}</Menu.GroupLabel>
        <Menu.RadioGroup value={activeCharityId}>
          {memberships.map((m) => {
            const active = m.charity_id === activeCharityId;
            return (
              <Menu.RadioItem
                key={m.charity_id}
                saas
                value={m.charity_id}
                testId={`org-switcher-option-${m.charity_id}`}
                onClick={() => {
                  if (active) return;
                  whenDiscardable(() => setActiveCharityId(m.charity_id));
                }}
                startIcon={<Icon icon={Building2} size={16} aria-hidden />}
                endIcon={
                  active ? (
                    <Icon icon={Check} size={16} className="text-brand-600" aria-hidden />
                  ) : undefined
                }
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{m.name}</span>
                  {roleLabel(m.role) !== null && (
                    <span className="truncate text-s12 font-w400 text-text-tertiary">
                      {roleLabel(m.role)}
                    </span>
                  )}
                </span>
              </Menu.RadioItem>
            );
          })}
        </Menu.RadioGroup>
      </Menu.Group>
      <Menu.Separator />
      {/* The whole remaining surface area of the claim flow for an admin who already has an org
          (D-CP-36). It is one line in a menu on purpose: a second claim is rare, and the portal
          should not spend permanent nav on it. */}
      <Menu.Item
        saas
        testId="org-switcher-claim-another"
        onClick={() => whenDiscardable(() => void navigate({ to: CLAIM_ROUTES.search }))}
        startIcon={<Icon icon={Plus} size={16} aria-hidden />}
      >
        Claim another organization
      </Menu.Item>
      {trailingSeparator && <Menu.Separator />}
    </>
  );
}

export function OrgSwitcher() {
  const { activeMembership, isMultiOrg, isReady } = useActiveOrg();

  // Nothing to show until an org is positively resolved (unclaimed / loading / errored → pages own it).
  if (!isReady || activeMembership === null) return null;

  return (
    <div className="flex flex-col px-12 pt-8" {...testIdProp('org-switcher')}>
      <Menu.Root>
        {/* `saas` stands the legacy focus glow down so the house pairing is the only focus treatment. */}
        <Menu.Trigger
          saas
          testId="org-switcher-trigger"
          aria-label={
            isMultiOrg
              ? `Switch organization — current organization: ${activeMembership.name}`
              : `Organization menu — current organization: ${activeMembership.name}`
          }
          className={cn(
            'group flex w-full items-center gap-10 rounded-lg border border-border-subtle bg-surface px-10 py-8 text-left transition-colors hover:bg-surface-hover',
            FOCUS_RING,
          )}
        >
          <Avatar name={activeMembership.name} size="sm" />
          <span className="min-w-0 flex-1">
            <span
              className="block truncate font-sans text-s13 font-w600 tracking-givmo text-text-strong"
              {...testIdProp('org-switcher-active-name')}
            >
              {activeMembership.name}
            </span>
            {roleLabel(activeMembership.role) !== null && (
              <span className="block truncate font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                {roleLabel(activeMembership.role)}
              </span>
            )}
          </span>
          <Icon icon={ChevronsUpDown} size={16} className="shrink-0 text-icon-muted" aria-hidden />
        </Menu.Trigger>
        <Menu.Content
          testId="org-switcher-menu"
          saas
          side="bottom"
          align="start"
          className="min-w-[232px]"
        >
          <OrgSwitcherMenuSection label="Your organizations" />
        </Menu.Content>
      </Menu.Root>
      <NewOrgAnnouncement />
    </div>
  );
}

/**
 * "You now administer <charity>" — the arrival of an approved claim, said once (CPA-WEB46).
 *
 * A claim is approved somewhere else, minutes later, by somebody else. Before this, the ONLY evidence
 * that it had happened was a second name inside a menu nobody had a reason to open: Jonathan claimed an
 * organization, came back to the console, and read the old organization's name in the top-left twice
 * over before concluding the portal was broken. The list refreshing is what makes the new organization
 * REACHABLE; this is what makes it NOTICED.
 *
 * ⚠ IT LIVES IN THE SIDEBAR, WHICH BELOW `lg` IS THE CLOSED NAV DRAWER. On a phone this says nothing
 * until the person opens the drawer, so a claimant approved while reading `/donations` gets the REFRESH
 * half of CPA-WEB46 and waits for the NOTICE half. Deliberately NOT relocated here — the moment's
 * placement is an open question with Jonathan, and a builder moving a user-visible element on its own
 * judgement is not how this program works.
 *
 * The live region is mounted empty rather than only alongside its text, because a `role="status"` that
 * appears at the same instant as its text is not reliably announced. ⚠ That holds from the SECOND
 * organization on, not for the first: `<OrgSwitcher>` renders nothing at all until `isReady`, so a
 * caller whose FIRST organization is approved gets the region and its text in one commit (the chrome
 * swaps from the onboarding frame to the shell in that same commit) and a screen reader may miss it.
 * Hoisting the region above that early return would close it; it waits on the same placement ruling.
 *
 * The switch action carries the same unsaved-changes guard as the menu's rows — it re-scopes the whole
 * app, which costs a half-edited profile exactly as much from here as it does from there.
 */
function NewOrgAnnouncement() {
  const { newlyAddedOrg, acknowledgeNewOrg, activeCharityId, setActiveCharityId } = useActiveOrg();
  const { hasUnsavedChanges, confirmDiscard } = useUnsavedChangesGuard();

  const isActive = newlyAddedOrg !== null && newlyAddedOrg.charity_id === activeCharityId;

  function switchToIt() {
    if (newlyAddedOrg === null) return;
    const charityId = newlyAddedOrg.charity_id;
    // Acknowledged either way: they read it and acted on it, so it has done its job even if they then
    // keep the edits they were warned about.
    acknowledgeNewOrg();
    if (!hasUnsavedChanges) {
      setActiveCharityId(charityId);
      return;
    }
    void confirmDiscard().then((confirmed) => {
      if (confirmed) setActiveCharityId(charityId);
    });
  }

  return (
    <div role="status" aria-live="polite">
      {newlyAddedOrg !== null && (
        <div
          className="mt-8 flex items-start gap-8 rounded-lg border border-brand-200 bg-brand-50 py-8 pl-10 pr-6"
          {...testIdProp('org-switcher-new-org')}
        >
          <Icon
            icon={CheckCircle2}
            size={16}
            className="mt-2 shrink-0 text-brand-600"
            aria-hidden
          />
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <p className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-secondary">
              You now administer{' '}
              <span className="font-w600 text-text-strong">{newlyAddedOrg.name}</span>
            </p>
            {!isActive && (
              <button
                type="button"
                onClick={switchToIt}
                className={cn(
                  'self-start rounded-md font-sans text-s12 font-w600 tracking-givmo text-brand-700 outline-none transition-colors hover:text-brand-800 motion-reduce:transition-none',
                  FOCUS_RING,
                )}
                {...testIdProp('org-switcher-new-org-switch')}
              >
                Switch to it
              </button>
            )}
          </div>
          {/* `-mt-6` centres the 32px control's glyph on the FIRST line of a message that wraps to
              three — without it the × floats against the middle of the block (same optical nudge as
              `org-modules-panel`'s row control). */}
          <IconButton
            aria-label="Dismiss"
            tone="ghost"
            size="sm"
            className="-mt-6"
            onClick={acknowledgeNewOrg}
            {...testIdProp('org-switcher-new-org-dismiss')}
          >
            <Icon icon={X} size={14} aria-hidden />
          </IconButton>
        </div>
      )}
    </div>
  );
}
